'use client';

import React from 'react';
import { Typography } from 'src/components';
import { Dropdown } from 'src/components/PulseUI';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

interface CrumbProps {
  crumb: MedScout.Breadcrumb;
  isLast?: boolean;
}

const Crumb: React.FC<CrumbProps> = ({ crumb, isLast = false }) => {
  const router = useRouter();
  const pathname = usePathname();

  if (crumb?.options) {
    const dropdownOptions = crumb.options.map((option) => ({
      label: option.name,
      onClick: () => router.push(option.href),
      href: option.href,
    }));

    return (
      <Dropdown
        options={dropdownOptions}
        ariaLabel={`${crumb.name} breadcrumb options`}
        slots={{
          trigger: {
            component: (
              <span
                className="flex items-center gap-1 font-semibold text-inherit hover:text-gray-900 cursor-pointer"
                data-testid="crumb-dropdown-trigger"
              >
                {crumb.name}{' '}
                <Icon icon={faChevronDown} size="sm" aria-hidden="true" />
              </span>
            ),
          },
          option: {
            component: (option: { label: string; href?: string }) => {
              const isCurrent = option.href === pathname;
              return (
                <div
                  className={`${
                    isCurrent
                      ? 'border-l-4 border-highlight bg-gray-100'
                      : 'border-l-4 border-transparent'
                  }`}
                  data-testid={`crumb-option-${option.label}`}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  <span className="pl-2">{option.label}</span>
                </div>
              );
            },
          },
        }}
      />
    );
  }

  if (isLast) {
    return (
      <Typography
        variant="body1"
        color="primary"
        bold
        aria-current="page"
        data-testid="crumb-current"
      >
        {crumb.name}
      </Typography>
    );
  }

  return (
    <Link
      href={crumb.href}
      className="no-underline text-gray-500 hover:text-gray-700"
      data-testid={`crumb-link-${crumb.name}`}
    >
      {crumb.name}
    </Link>
  );
};

export default Crumb;
